import { useMemo } from "react";
import { MapContainer, TileLayer, Polyline, Marker, Popup } from "react-leaflet";
import type { LatLngExpression } from "leaflet";
import { LatLngBounds } from "leaflet";

import type { ConsignmentDetail } from "../types";

type ConsignmentEvent = ConsignmentDetail["events"][number];

type Props = {
  events: ConsignmentEvent[];
};

type RoutePoint = {
  position: LatLngExpression;
  event: ConsignmentEvent;
  kind: "start" | "end" | "middle";
};

export function ConsignmentRouteMap({ events }: Props) {
  const geoPoints = useMemo<RoutePoint[]>(() => {
    const withCoords = events
      .map((event, index) => ({
        event,
        index,
        coords:
          typeof event.latitude === "number" && typeof event.longitude === "number"
            ? ([event.latitude, event.longitude] as LatLngExpression)
            : null,
      }))
      .filter((item): item is { event: ConsignmentEvent; index: number; coords: LatLngExpression } => Boolean(item.coords));

    if (withCoords.length === 0) {
      return [];
    }

    const sorted = [...withCoords].sort((a, b) => {
      const timeA = a.event.timestamp ? new Date(a.event.timestamp).getTime() : NaN;
      const timeB = b.event.timestamp ? new Date(b.event.timestamp).getTime() : NaN;

      if (Number.isFinite(timeA) && Number.isFinite(timeB)) {
        return timeA - timeB;
      }
      if (Number.isFinite(timeA)) return -1;
      if (Number.isFinite(timeB)) return 1;
      return a.index - b.index;
    });

    return sorted.map((item, idx) => ({
      position: item.coords,
      event: item.event,
      kind: idx === 0 ? "start" : idx === sorted.length - 1 ? "end" : "middle",
    }));
  }, [events]);

  const mapBounds = useMemo(() => {
    if (geoPoints.length < 2) return null;
    const bounds = new LatLngBounds(geoPoints.map((point) => point.position as [number, number]));
    return bounds.pad(0.15);
  }, [geoPoints]);

  if (geoPoints.length === 0) {
    return <p>Aucun point géolocalisé disponible pour cette expédition.</p>;
  }

  const positions = geoPoints.map((point) => point.position);
  const center = geoPoints[Math.floor(geoPoints.length / 2)].position;
  const baseMapProps = mapBounds
    ? { bounds: mapBounds as LatLngBounds }
    : { center, zoom: 6 as number };

  return (
    <div className="route-map">
      <MapContainer
        {...baseMapProps}
        scrollWheelZoom
        wheelDebounceTime={120}
        wheelPxPerZoomLevel={140}
        style={{ height: "380px", width: "100%", borderRadius: "14px", overflow: "hidden" }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/">OSM</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {positions.length > 1 ? (
          <Polyline positions={positions} pathOptions={{ color: "#2563eb", weight: 4, opacity: 0.7 }} />
        ) : null}
        {geoPoints.map((point, idx) => (
          <Marker key={`${point.event.timestamp ?? idx}`} position={point.position}>
            <Popup>
              <div style={{ minWidth: "210px" }}>
                <strong>
                  {point.kind === "start" ? "Départ" : point.kind === "end" ? "Arrivée" : point.event.typeCode ?? "Étape"}
                </strong>
                <br />
                {point.event.timestamp
                  ? new Date(point.event.timestamp).toLocaleString("fr-FR")
                  : "Horodatage inconnu"}
                <br />
                {point.event.iataCode || point.event.country ? (
                  <span style={{ color: "#0f172a" }}>
                    {point.event.iataCode ? `${point.event.iataCode} · ` : ""}
                    {point.event.country ?? ""}
                  </span>
                ) : null}
                {point.event.description ? (
                  <>
                    <br />
                    <small style={{ color: "#475569" }}>{point.event.description}</small>
                  </>
                ) : null}
              </div>
            </Popup>
          </Marker>
        ))}
      </MapContainer>
    </div>
  );
}
